import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AppHeader } from "@/components/sartn/AppHeader";
import { FeatureStatusBanner } from "@/components/sartn/FeatureStatusBanner";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { useChannelStore } from "@/stores/channelStore";
import type { PlanDuration } from "@/stores/subscriptionStore";
import { cn } from "@/lib/cn";

const STAGGER_FIELDS = 0.05;

const DURATION_OPTIONS: {
  value: PlanDuration;
  label: string;
  days: number;
}[] = [
  { value: "weekly", label: "Weekly", days: 7 },
  { value: "monthly", label: "Monthly", days: 30 },
  { value: "quarterly", label: "Quarterly", days: 90 },
  { value: "yearly", label: "Yearly", days: 365 },
];

interface FormErrors {
  name?: string;
  price?: string;
}

function validatePlanForm(name: string, price: string): FormErrors {
  const errors: FormErrors = {};
  if (name.trim().length < 2) {
    errors.name = "Plan name must be at least 2 characters";
  }
  const priceNum = Number(price);
  if (!price || isNaN(priceNum) || priceNum <= 0) {
    errors.price = "Price must be greater than 0";
  }
  return errors;
}

function calculateDailyRate(price: number, days: number): string {
  if (days === 0 || price <= 0) return "0";
  return (price / days).toFixed(1);
}

function FormField({
  label,
  error,
  index,
  children,
}: {
  label: string;
  error?: string;
  index: number;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * STAGGER_FIELDS, duration: 0.2 }}
      className="flex flex-col gap-1.5"
    >
      <label className="text-sm font-medium text-[var(--color-text)]">
        {label}
      </label>
      {children}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </motion.div>
  );
}

const inputClass = cn(
  "w-full rounded-[var(--radius-md)] border border-[var(--color-border)]",
  "bg-[var(--color-surface)] px-3 py-2.5 text-sm text-[var(--color-text)]",
  "placeholder:text-[var(--color-text-muted)]",
  "focus:border-[var(--color-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]",
  "transition-colors",
);

function DurationSelector({
  selected,
  price,
  onSelect,
}: {
  selected: PlanDuration;
  price: number;
  onSelect: (duration: PlanDuration) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {DURATION_OPTIONS.map((option) => {
        const isSelected = selected === option.value;
        const dailyRate = calculateDailyRate(price, option.days);

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onSelect(option.value)}
            className={cn(
              "flex flex-col items-center gap-1 rounded-[var(--radius-md)] border p-3",
              "text-center transition-all active:scale-[0.97]",
              isSelected
                ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10"
                : "border-[var(--color-border)] bg-[var(--color-surface)]",
            )}
          >
            <span
              className={cn(
                "text-sm font-semibold",
                isSelected
                  ? "text-[var(--color-primary)]"
                  : "text-[var(--color-text)]",
              )}
            >
              {option.label}
            </span>
            <span className="text-xs text-[var(--color-text-muted)]">
              {option.days} days
            </span>
            {price > 0 && (
              <span className="text-[10px] text-[var(--color-text-muted)]">
                ~{dailyRate} ETB/day
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function PlanForm() {
  const addPlan = useSubscriptionStore((state) => state.addPlan);
  const channels = useChannelStore((state) => state.channels);
  const fetchChannels = useChannelStore((state) => state.fetchChannels);

  const [name, setName] = useState("");
  const [channelId, setChannelId] = useState("");
  const [price, setPrice] = useState("");
  const [duration, setDuration] = useState<PlanDuration>("monthly");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    void fetchChannels();
  }, [fetchChannels]);

  useEffect(() => {
    if (!channelId && channels[0]) setChannelId(channels[0].chatId);
  }, [channelId, channels]);

  function navigateBack() {
    window.location.hash = "/subscriptions";
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    const validationErrors = validatePlanForm(name, price);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSaving(true);

    try {
      await addPlan({
        channelId,
        name: name.trim(),
        description: description.trim() || undefined,
        price: Number(price),
        duration,
      });
      navigateBack();
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title="New Plan" onBack={navigateBack} />

      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="flex-1 px-4 pb-20 pt-4"
      >
        <FeatureStatusBanner
          status="connected"
          title="Live subscription plan / ቀጥታ የሰብስክሪፕሽን ፕላን"
          description="Channels and new plans are loaded and saved through the authenticated API. / ቻናሎችና አዲስ ፕላኖች በተረጋገጠ API ይጫናሉ እና ይቀመጣሉ።"
        />

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField label="Plan Name" error={errors.name} index={0}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Basic Monthly"
              className={inputClass}
            />
          </FormField>

          <FormField label="Channel" index={1}>
            <select
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              className={inputClass}
            >
              {channels.length === 0 && <option value="">No managed channels</option>}
              {channels.map((channel) => (
                <option key={channel.id} value={channel.chatId}>
                  {channel.title}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Price (ETB)" error={errors.price} index={2}>
            <div className="relative">
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                min="0"
                step="1"
                className={cn(inputClass, "pr-12")}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-[var(--color-text-muted)]">
                ETB
              </span>
            </div>
          </FormField>

          <FormField label="Duration" index={3}>
            <DurationSelector
              selected={duration}
              price={Number(price) || 0}
              onSelect={setDuration}
            />
          </FormField>

          <FormField label="Description" index={4}>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional: describe what subscribers get..."
              rows={3}
              className={cn(inputClass, "resize-none")}
            />
          </FormField>

          <motion.button
            type="submit"
            disabled={isSaving || !channelId}
            whileTap={{ scale: 0.97 }}
            className={cn(
              "mt-4 w-full rounded-[var(--radius-md)] py-3 text-sm font-semibold text-white",
              "transition-colors active:scale-[0.97]",
              isSaving || !channelId
                ? "cursor-not-allowed bg-[var(--color-primary)]/60"
                : "bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)]",
            )}
          >
            {isSaving ? "Creating..." : channelId ? "Create Plan" : "Connect a channel first"}
          </motion.button>
        </form>
      </motion.main>
    </div>
  );
}
